import type { Expert, ExpertEvidence, RankBy } from "../types";
import { CITATIONS_SOURCE, citedByText, RANK_BY_LABELS, rankValueText, yearsOnTopic } from "../utils/factors";
import { paperUrl, profileUrl } from "../utils/links";

const POSITION_LABELS: Record<ExpertEvidence["author_position"], string> = {
  first: "first author",
  last: "last author",
  middle: "middle author",
  sole: "sole author",
};

const PaperRow = ({ ev }: { ev: ExpertEvidence }) => {
  const p = ev.paper;
  const url = paperUrl(p);
  const review = (ev.review ?? 0) >= 0.5;
  return (
    <li className="xp-paper">
      {p.foundational || review ? (
        <div className="ch-badges">
          {p.foundational ? (
            <span
              className="ch-badge"
              title="A landmark paper search missed: one the on-topic papers read cite most, not counting its authors' own"
            >
              Foundational · {citedByText(p.cited_by_candidates ?? 0)}
            </span>
          ) : null}
          {review ? (
            <span className="ch-badge weak" title="Judged a review article: it counts for 30%">
              Review
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="ch-title">
        {url ? (
          <a href={url} target="_blank" rel="noreferrer">
            {p.title}
          </a>
        ) : (
          p.title
        )}
      </div>
      <div className="ch-meta">
        <span>{POSITION_LABELS[ev.author_position]}</span>
        {p.year ? <span>{p.year}</span> : null}
        {p.journal ? <span>{p.journal}</span> : null}
        {p.citations != null ? (
          <span title={CITATIONS_SOURCE}>{p.citations.toLocaleString("en-US")} citations</span>
        ) : null}
        {!p.foundational && p.cited_by_candidates ? (
          <span title="On-topic papers read that cite it, not counting its authors' own">
            {citedByText(p.cited_by_candidates)}
          </span>
        ) : null}
        {ev.relevance != null ? (
          <span title="The model's probability that the topic is a main subject of this paper">
            P(on-topic) {ev.relevance.toFixed(2)}
          </span>
        ) : null}
      </div>
      {ev.evidence ? (
        <p className="xp-quote">
          <mark className="ch-ev answer">{ev.evidence}</mark>
        </p>
      ) : null}
    </li>
  );
};

export const ExpertCard = ({
  expert,
  rank,
  recentSince,
  rankBy = "score",
}: {
  expert: Expert;
  rank: number;
  recentSince?: number | null;
  rankBy?: RankBy;
}) => {
  const url = profileUrl(expert);
  return (
    <li className="ch-card xp-card">
      <div className="xp-head">
        <span className="xp-rank">{rank}</span>
        <span className="xp-name">
          {url ? (
            <a href={url} target="_blank" rel="noreferrer">
              {expert.name}
            </a>
          ) : (
            expert.name
          )}
        </span>
        <span
          className="xp-score"
          title={
            rankBy === "score"
              ? "Sum of this person's paper weights"
              : `Ranked by ${RANK_BY_LABELS[rankBy].toLowerCase()} over the on-topic papers counted (see How are people ranked?)`
          }
        >
          {rankValueText(expert, rankBy)}
        </span>
      </div>
      <div className="ch-byline">
        {[
          expert.affiliation,
          `${expert.papers} ${expert.papers === 1 ? "paper" : "papers"} on the topic (${expert.lead_papers} as first or last author)`,
          yearsOnTopic(expert, recentSince),
          `${expert.citations.toLocaleString("en-US")} citations`,
          expert.h_index != null ? `h-index ${expert.h_index}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
      <ul className="xp-papers">
        {expert.evidence.map((ev, i) => (
          <PaperRow key={ev.paper.doi ?? ev.paper.id ?? i} ev={ev} />
        ))}
      </ul>
    </li>
  );
};
